import {ApiClient} from '@hconnect/apiclient'
import {useMutation, useQuery} from '@tanstack/react-query'

import {usePlantUpmId} from '../../shared/hooks/usePlantUpmId'

export enum SubjectCategory {
  OnEventContextualAnomaly = 'OnEventContextualAnomaly',
  DailyContextualAnomaly = 'DailyContextualAnomaly',
  OnEventThresholdViolation = 'OnEventThresholdViolation',
  DailyThresholdViolation = 'DailyThresholdViolation',
  OnEventContextualAnomalySignal = 'OnEventContextualAnomalySignal',
  DailyContextualAnomalySignal = 'DailyContextualAnomalySignal',
  OnEventThresholdViolationSignal = 'OnEventThresholdViolationSignal',
  DailyThresholdViolationSignal = 'DailyThresholdViolationSignal'
}

export enum FeedbackRating {
  Positive = 'Positive',
  Negative = 'Negative'
}

export const feedbackCategories = [
  'UnreasonablePrediction',
  'RelatesToPlantShutdown',
  'RedundantAlerts',
  'OngoingMaintenanceIssue',
  'ProcessRelatedReasons',
  'Other'
]

export type FeedbackCategory = (typeof feedbackCategories)[number]

export const useGetFeedback = ({
  alertInstanceId,
  subjectCategory
}: {
  alertInstanceId?: string
  subjectCategory?: SubjectCategory
}) => {
  const plantUpmId = usePlantUpmId()

  return useQuery({
    queryKey: ['feedback', plantUpmId, alertInstanceId, subjectCategory],
    queryFn: async () => {
      const path = '/kms/feedback/instances'
      return (
        await ApiClient.axiosInstance.get<FeedbackResponse[]>(path, {
          params: {
            plantUpmId,
            alertInstanceId,
            subjectCategory
          }
        })
      ).data
    },
    refetchOnMount: false,
    refetchOnWindowFocus: false,
    enabled: Boolean(alertInstanceId) && Boolean(subjectCategory)
  })
}

type FeedbackResponse = {
  alertInstanceId: string
  subjectCategory: SubjectCategory
  plantUpmId: string
  rating: FeedbackRating
  categories: FeedbackCategory[]
  comment?: string
}

type FeedbackTimeRange = {
  start?: string
  end?: string
}

export type PostFeedbackPayload = {
  alertInstanceId: string
  subjectCategory: SubjectCategory
  localNames: string[]
  alertTimeRange: FeedbackTimeRange
  rating: FeedbackRating
  comment?: string
  categories?: FeedbackCategory[]
  signalTimeRange?: FeedbackTimeRange
}

export const usePostFeedback = () => {
  const plantUpmId = usePlantUpmId()

  return useMutation({
    mutationFn: async (payload: PostFeedbackPayload) => {
      const path = `/kms/${plantUpmId}/feedback/instances`

      return (await ApiClient.axiosInstance.post<void>(path, payload)).data
    }
  })
}
